// src/lib/ai/fieldPrompts.ts

export const bestPractices: Record<string, { why: string; tips: string[]; related?: string[]; limits?: string }>= {
  'site.brand': {
    why: 'Core Identity. Use your formal, registered business name consistently across the site.',
    tips: [
      'Input: Your official business name (e.g., Frosted Cake Art).'
    ],
    related: ['site.tagline']
  },
  'site.tagline': {
    why: 'Quick Descriptor. A concise phrase that immediately summarizes your business and location.',
    tips: [
      'Input: A short, punchy line with a primary product/service and location (e.g., Luxury Custom Cakes & Sculpted Sugar Art in Chagrin Falls, OH).'
    ],
    related: ['site.brand']
  },
  'site.default_meta.desc': {
    why: 'Global Fallback. The standard text search engines use when a specific page description is missing.',
    tips: [
      'Best Practice: Keep under 160 characters. Include your primary service, location, and a unique selling proposition (e.g., "award-winning").'
    ],
    related: ['page.meta_description','page.og_description'],
    limits: '160 chars'
  },
  'page:/.meta_title': {
    why: 'The Click Magnet (SERP). The most important on-page SEO element. Appears in browser tabs and search results.',
    tips: [
      'Best Practice: Keep between 50–60 characters. Must be unique for every page. Use primary keywords near the start. Format: "Primary Keyword …"'
    ],
    related: ['page.h1','page.og_title'],
    limits: '50–60 chars'
  },
  'page:/.meta_description': {
    why: 'Search Snippet. Summarizes the content of the specific page for search results.',
    tips: [
      'Best Practice: Keep between 150–160 characters. Encourage clicks by using a clear call-to-action or benefit statement.'
    ],
    related: ['page.meta_title','page.og_description'],
    limits: '150–160 chars'
  },
  'page:/.h1': {
    why: 'On-Page Heading. The single main heading (<h1>) that tells the visitor and search engine what the page is about.',
    tips: [
      'Best Practice: Should contain the page\'s primary keyword and be unique. It should be close to, but not exactly identical to, the Meta Title.'
    ],
    related: ['page.meta_title']
  },
  'page:/.canonical': {
    why: 'Duplicate Content Resolver. Tells search engines which version of a page is the definitive original version.',
    tips: [
      'Best Practice: Must be an absolute URL (e.g., https://www.yoursite.com/page). Use this field only when a page can be accessed via multiple URLs (e.g., /index.html and /).'
    ],
    related: ['page.meta_title']
  },
  'page:/.og_title': {
    why: 'Social Share Title. The title that appears when the page is shared on platforms like Facebook or LinkedIn.',
    tips: [
      'Best Practice: Can be slightly longer than Meta Title (up to ~70 chars). Use emotional language or a clear benefit statement.'
    ],
    related: ['page.meta_title','page.og_description'],
    limits: '~70 chars'
  },
  'page:/.og_description': {
    why: 'Social Share Snippet. The description that appears when the page is shared.',
    tips: [
      'Best Practice: Keep under 200 characters. Must be highly compelling for social media context.'
    ],
    related: ['page.meta_description','page.og_title'],
    limits: '200 chars'
  },
  'page:/.og_image': {
    why: 'Social Thumbnail. The image used when the page is shared.',
    tips: [
      'Best Practice: Must be a public, absolute URL. Image should have an optimal aspect ratio (e.g., 1200x630 pixels or 1.91:1).'
    ],
    related: ['page.og_title','page.og_description']
  },
  'page:/.keywords': {
    why: 'Page Context (Optional). A comma-separated list of terms relevant to the page.',
    tips: [
      'Best Practice: Limit to 8-12 relevant terms. Focus on long-tail keywords (e.g., wedding cake tasting cleveland) over single words.'
    ],
    related: ['page.meta_title','page.h1']
  },
  'page:/.internal_links': {
    why: 'Crawler Guidance. A list of related pages you want crawlers to prioritize.',
    tips: [
      'Best Practice: List 3–5 highly relevant, relative paths (one per line) that link to other pages on your site (e.g., /gallery).'
    ],
    related: ['page.canonical']
  }
};
